<!-- Banner.vue -->
<template>
  <div class="banner">
    <div class="banner-content">
      <div class="banner-slides">
        <div v-for="(slide, index) in bannerMovies" :key="`slide-${index}`" class="banner-slide"
          :class="{'active': currentIndex === index || (index === 0 && !autoSlideEnabled)}">
          <img :src="slide.image" :alt="slide.title">
        </div>
        <div class="shadow"></div>
        <div v-for="(slide, index) in bannerMovies" :key="`info-${index}`"
          class=" banner-slide-info"
          :class="{'active': currentIndex === index || (index === 0 && !autoSlideEnabled)}">
          <div class="info-wrapper">
            <div class="info-content">
              <h2 class="movie-title">{{ slide.title }}</h2>
              <div class="movie-meta">
                <div class="rating">
                  <img class="star-icon" src="@/assets/icon/star.svg" alt="rating">
                  <span class="rating-value">{{ Number(slide.rating).toFixed(1) }}</span>
                </div>
                <div class="divider"></div>
                <div class="release-info">{{ slide.releaseDate }} · {{ slide.genre }}</div>
              </div>
              <p class="movie-description">{{ slide.description }}</p>
              <button class="btn-action btn-hover" @click="navigateToDetail(slide)">
                <img class="play-icon" src="@/assets/icon/play-circle.svg" alt="play">
                <span>Watch Trailer</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="banner-dots">
        <span v-for="(dot, index) in bannerMovies" :key="`dot-${index}`"
          :class="['dot', { active: currentIndex === index }]" @click="setSlide(index)"></span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Banner',
  props: {
    bannerMovies: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      currentIndex: 0,
      autoSlideEnabled: false,
      timer: null,
      // 默认数据，仅在没有传入bannerMovies时使用
      defaultSlides: [
        {
          id: 1234567, // 添加默认ID
          title: 'Thunderbolts*',
          image: require('@/assets/banner.png'),
          rating: '7.3',
          releaseDate: '2025-04-30',
          genre: 'Action',
          description: 'After finding themselves ensnared in a death trap, seven disillusioned castoffs must embark on a dangerous mission that will force them to confront the darkest corners of their pasts.'
        }
      ]
    }
  },
  computed: {
    slides () {
      // 如果传入了bannerMovies且不为空，则使用它，否则使用默认数据
      return (this.bannerMovies && this.bannerMovies.length > 0)
        ? this.bannerMovies
        : this.defaultSlides;
    }
  },
  watch: {
    bannerMovies: {
      handler () {
        // 当banner数据变化时，重置当前索引并重启自动轮播
        this.currentIndex = 0;
        if (this.autoSlideEnabled) {
          this.stopAutoSlide();
          this.startAutoSlide();
        }
        this.preloadImages();
      },
      immediate: true
    }
  },
  mounted () {
    this.startAutoSlide();
    this.preloadImages();
  },
  beforeDestroy () {
    this.stopAutoSlide();
  },
  methods: {
    navigateToDetail (movie) {
      // 格式化电影名称为URL友好的格式
      const formattedName = movie.title ? movie.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]/g, '') : 'movie';

      // 需要获取电影ID，从传入的电影对象中获取
      const movieData = {
        id: movie.id, // 使用真实ID
        title: movie.title,
        poster: movie.image,
        genres: [movie.genre],
        score: movie.rating,
        date: movie.releaseDate,
        description: movie.description || '',
        type: 'movie' // 标记为电影类型
      };

      // 保存数据到本地存储
      localStorage.setItem('selectedMovie', JSON.stringify(movieData));

      // 跳转到详情页 - 使用真实ID
      this.$router.push(`/movies/${formattedName}/${movie.id}/detail`);
    },
    startAutoSlide () {
      this.autoSlideEnabled = true;
      this.timer = setInterval(() => {
        this.nextSlide();
      }, 5000); // 5分钟切换一次
    },
    stopAutoSlide () {
      this.autoSlideEnabled = false;
      clearInterval(this.timer);
    },
    nextSlide () {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    },
    prevSlide () {
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    },
    setSlide (index) {
      this.currentIndex = index;
      // 重置自动轮播计时器
      if (this.autoSlideEnabled) {
        this.stopAutoSlide();
        this.startAutoSlide();
      }
    },
    preloadImages () {
      // 预加载所有图片以避免切换时的闪烁
      // 检查是否在浏览器环境中
      if (typeof window !== 'undefined') {
        this.slides.forEach(slide => {
          if (typeof slide.image === 'string') {
            const img = new Image();
            img.src = slide.image;
          }
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.banner {
	position: relative;
	overflow: hidden;

	.shadow {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(180deg, rgba(13, 12, 15, 0.8) 0%, rgba(13, 12, 15, 0) 19.9%, rgba(13, 12, 15, 0) 39.8%, rgba(13, 12, 15, 0.9) 69.91%, #0d0c0f 100%);
		z-index: 2;
	}

	.banner-content {
		position: relative;
		width: 100%;
	}

	.banner-slides {
		position: relative;
		width: 100%;
		// min-height: 500px; /* 设置最小高度确保轮播容器始终有高度 */
		height: rem(760);
		overflow: hidden;
	}

	.banner-slide {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		visibility: hidden;
		transition: opacity 1s ease, visibility 1s ease;
		z-index: 0;

		&.active {
			opacity: 1;
			visibility: visible;
			z-index: 1;
			position: relative; /* 活动的幻灯片设置为相对定位，确保它撑开容器 */
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
			z-index: 1;
		}
	}

	.banner-slide-info {
		z-index: 2 !important;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		opacity: 0;
		visibility: hidden;
		transition: opacity 1s ease, visibility 1s ease;

		&.active {
			opacity: 1;
			visibility: visible;
			z-index: 1;
			position: relative; /* 活动的幻灯片设置为相对定位，确保它撑开容器 */
		}
	}

	.info-wrapper {
		position: absolute;
		bottom: rem(40);
		left: 0;
		width: 100%;
		padding: 0 rem(80) 0 rem(80);
		z-index: 3;
		display: flex;
		flex-direction: row;
		align-items: flex-end;
		justify-content: space-between;
	}

	.info-content {
		display: flex;
		flex-direction: column;
		gap: rem(20);
		align-items: flex-start;
		justify-content: flex-start;
		width: rem(520);
		max-width: rem(520);
		// margin-bottom: rem(40);
	}

	.movie-title {
		color: #ffffff;
		font-family: 'Inter-Black', sans-serif;
		font-size: rem(32);
		line-height: rem(36);
		font-weight: 900;
		margin: 0;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.movie-meta {
		display: flex;
		flex-direction: row;
		gap: rem(12);
		align-items: center;
	}

	.rating {
		display: flex;
		flex-direction: row;
		gap: rem(8);
		align-items: center;
	}

	.star-icon {
		width: rem(12);
		height: rem(12);
	}

	.rating-value {
		color: #ffaf00;
		font-family: 'Inter-Bold', sans-serif;
		font-size: rem(16);
		line-height: rem(16);
		font-weight: 700;
		text-transform: uppercase;
	}

	.divider {
		background: #ffffff;
		opacity: 0.1;
		width: 1px;
		height: rem(20);
	}

	.release-info {
		color: #ffffff;
		font-family: 'Inter-Regular', sans-serif;
		font-size: rem(14);
		line-height: rem(14);
		font-weight: 400;
		opacity: 0.6;
		flex: 1;
	}

	.movie-description {
		color: #ffffff;
		font-family: 'Inter-Regular', sans-serif;
		font-size: rem(14);
		line-height: rem(20);
		font-weight: 400;
		margin: 0;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.btn-action {
		background: #fe324b;
		border-radius: rem(100);
		padding: rem(12) rem(16);
		display: flex;
		flex-direction: row;
		gap: rem(12);
		align-items: center;
		border: none;
		cursor: pointer;
		margin-top: rem(20);
	}

	.btn-hover:hover {
		opacity: 0.7;
	}

	.play-icon {
		width: rem(20) !important;
		height: rem(20);
	}

	.btn-action span {
		color: #ffffff;
		font-family: 'Inter-Bold', sans-serif;
		font-size: rem(16);
		line-height: rem(16);
		font-weight: 700;
	}

	.banner-dots {
		display: flex;
		flex-direction: row;
		gap: rem(12);
		align-items: center;
		position: absolute;
		bottom: rem(40);
		right: rem(80);
		z-index: 3;
	}

	.dot {
		background: #ffffff;
		border-radius: 50%;
		width: rem(8);
		height: rem(8);
		opacity: 0.4;
		cursor: pointer;
	}

	.dot.active {
		opacity: 1;
	}
}

// PC端基础样式
.detect_deviece_pc,
.detect_deviece_desktop {
	.banner {
		margin-top: rem(-80);
	}
}

// 移动端基础样式
.detect_deviece_mobile {
	.banner {
		margin-top: rem(-76);

		.banner-slides {
			height: rem(800);
		}

		.info-wrapper {
			position: absolute;
			bottom: 0;
			left: 50%;
			transform: translateX(-50%);
			width: 100%;
			padding: 0 rem(16);
			z-index: 3;
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			justify-content: flex-start;
			gap: rem(16);
		}

		.info-content {
			width: 100%;
			max-width: 100%;
			display: flex;
			flex-direction: column;
			gap: rem(20);
			align-items: flex-start;
			justify-content: flex-start;
			align-self: stretch;
			margin-top: rem(486);
		}

		.movie-meta {
			flex-direction: column;
			align-items: flex-start;
			gap: rem(8);
		}

		.rating {
			display: none;
		}

		.divider {
			display: none;
		}

		.release-info {
			font-size: rem(14);
			line-height: rem(14);
			opacity: 0.6;
			align-self: stretch;
		}

		.movie-description {
			font-size: rem(14);
			line-height: rem(20);
			align-self: stretch;
		}

		.btn-action {
			margin-top: rem(20);
			padding: rem(12) rem(16);
			gap: rem(12);
			align-items: center;
			justify-content: flex-start;
			flex-shrink: 0;
		}

		.banner-dots {
			position: relative;
			bottom: auto;
			right: auto;
			display: flex;
			flex-direction: row;
			gap: rem(12);
			align-items: center;
			justify-content: flex-start;
			flex-shrink: 0;
			height: rem(44);
			padding: 0 rem(16);
			margin-top: rem(16);

			.dot {
				flex-shrink: 0;
				width: rem(8);
				height: rem(8);
				position: relative;
				aspect-ratio: 1;

				&.active {
					opacity: 1;
				}
			}
		}
	}
}
</style> 